import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  Crown,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserRound,
  Users,
  UserX,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, Modal, PageHeader, timeAgo } from '../components/ui';

type Role = 'OWNER' | 'ADMIN' | 'OPERATOR' | 'VIEWER';
export interface OrgUser {
  id: string;
  userId: string;
  email: string;
  name: string;
  role: Role;
  siteIds: string[];
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export const ROLES: Record<Role, { label: string; desc: string; fg: string; bg: string }> = {
  OWNER: { label: 'Owner', desc: 'Everything, including users and billing', fg: '#b45309', bg: '#fff4e0' },
  ADMIN: { label: 'Admin', desc: 'All settings and content, and users', fg: '#6d28d9', bg: '#f1ebff' },
  OPERATOR: { label: 'Operator', desc: 'Day-to-day content, guests and messages', fg: '#1d4ed8', bg: '#e8f0ff' },
  VIEWER: { label: 'Viewer', desc: 'Read only', fg: '#475569', bg: '#eef1f5' },
};
const RANK: Record<Role, number> = { VIEWER: 0, OPERATOR: 1, ADMIN: 2, OWNER: 3 };
const initials = (u: { name: string; email: string }) =>
  (u.name || u.email)
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** A readable random password: 4 groups of 4 (letters and digits, no look-alikes). */
export function generatePassword() {
  const abc = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const buf = new Uint32Array(16);
  crypto.getRandomValues(buf);
  const s = Array.from(buf, (n) => abc[n % abc.length]).join('');
  return `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8, 12)}-${s.slice(12)}`;
}

/** "My account | Users" tabs at the top of Account settings. */
export function AccountTabs() {
  const { can } = useSession();
  if (!can('ADMIN')) return null;
  return (
    <div className="line-tabs" style={{ marginTop: 0 }}>
      <NavLink to="/account" end className={({ isActive }) => (isActive ? 'on' : '')}>
        <UserRound size={17} /> My account
      </NavLink>
      <NavLink to="/account/users" className={({ isActive }) => (isActive ? 'on' : '')}>
        <Users size={17} /> Users
      </NavLink>
    </div>
  );
}

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function UsersPage() {
  const { me, sites, can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: OrgUser[] }>('/users').then((r) => r.items), enabled: can('ADMIN') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<OrgUser> }) => api.patch(`/users/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/users/${id}`), onSuccess: invalidate });
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [resetFor, setResetFor] = useState<OrgUser | null>(null);
  const all = users.data ?? [];
  const items = all.filter((u) => {
    const s = q.trim().toLowerCase();
    if (s && ![u.name, u.email].some((x) => x.toLowerCase().includes(s))) return false;
    if (role !== 'all' && u.role !== role) return false;
    if (status === 'active' && !u.isActive) return false;
    if (status === 'inactive' && u.isActive) return false;
    return true;
  });
  const week = Date.now() - 7 * 86_400_000;
  const siteNames = (ids: string[]) => (ids.length ? ids.map((id) => sites.find((s) => s.id === id)?.name ?? '…').join(', ') : 'All sites');
  const canManage = (u: OrgUser) => u.userId !== me.user.id && RANK[u.role] <= RANK[me.role] && (u.role !== 'OWNER' || me.role === 'OWNER');

  if (!can('ADMIN'))
    return (
      <>
        <PageHeader title="Account settings" sub="Your profile, security and sign-in." />
        <div className="card empty">Only admins can manage users.</div>
      </>
    );
  return (
    <>
      <PageHeader
        title="Account settings"
        sub="Who can sign in to the PulseIPTV admin, with which role and for which sites."
        actions={
          <button className="btn primary" onClick={() => navigate('/account/users/new')}>
            <Plus size={16} /> Add user
          </button>
        }
      />
      <AccountTabs />
      <div className="stat-strip">
        <StatCard icon={<Users size={22} />} tone="violet" value={all.length} label="Users" sub={me.organization.name} />
        <StatCard icon={<UserCheck size={22} />} tone="green" value={all.filter((u) => u.isActive).length} label="Active" sub="Can sign in" />
        <StatCard icon={<Crown size={22} />} tone="amber" value={all.filter((u) => u.role === 'OWNER' || u.role === 'ADMIN').length} label="Owners & admins" sub="Can change settings" />
        <StatCard icon={<ShieldCheck size={22} />} tone="blue" value={all.filter((u) => u.lastLoginAt && new Date(u.lastLoginAt).getTime() > week).length} label="Signed in this week" sub="Last 7 days" />
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
            <option value="all">All roles</option>
            {(Object.keys(ROLES) as Role[]).map((r) => (
              <option key={r} value={r}>
                {ROLES[r].label}
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Deactivated</option>
          </Select>
        </div>
        {users.isLoading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>{all.length ? 'No user matches.' : 'No users yet.'}</Empty>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="fac-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Site access</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th>Added</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const r = ROLES[u.role];
                  const self = u.userId === me.user.id;
                  const manage = canManage(u);
                  return (
                    <tr key={u.id} style={!u.isActive ? { opacity: 0.6 } : undefined} onClick={() => manage && navigate(`/account/users/${u.id}`)}>
                      <td>
                        <span className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
                          <span className="user-avatar">{initials(u)}</span>
                          <span style={{ minWidth: 0 }}>
                            <strong style={{ display: 'block' }}>
                              {u.name} {self && <span className="badge brand">You</span>}
                            </strong>
                            <span className="muted" style={{ fontSize: 12.5 }}>
                              {u.email}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className="chip" style={{ color: r.fg, background: r.bg }}>
                          {u.role === 'OWNER' && <Crown size={11} style={{ verticalAlign: -1 }} />} {r.label}
                        </span>
                      </td>
                      <td className="muted" style={{ fontSize: 13, maxWidth: 220 }}>
                        {siteNames(u.siteIds)}
                      </td>
                      <td>
                        <span className={`state-pill ${u.isActive ? 'online' : 'disabled'}`}>
                          <span className="state-dot" /> {u.isActive ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      <td className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {u.lastLoginAt ? timeAgo(u.lastLoginAt) : 'Never'}
                      </td>
                      <td className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {new Date(u.createdAt).toLocaleDateString([], { dateStyle: 'medium' })}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {self ? (
                          <button className="btn small" onClick={() => navigate('/account')}>
                            My account
                          </button>
                        ) : manage ? (
                          <>
                            <button className="icon-btn" title="Edit" onClick={() => navigate(`/account/users/${u.id}`)}>
                              <Pencil size={16} />
                            </button>
                            <Menu
                              minWidth={210}
                              button={({ onClick }) => (
                                <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                  <MoreHorizontal size={16} />
                                </button>
                              )}
                            >
                              <button type="button" className="more-item" onClick={() => setResetFor(u)}>
                                <KeyRound size={15} /> Set a new password
                              </button>
                              {u.isActive ? (
                                <button
                                  type="button"
                                  className="more-item"
                                  onClick={() => ask(`Deactivate ${u.name}? They are signed out and cannot sign in until you activate them again.`, () => patch.mutate({ id: u.id, body: { isActive: false } }), { action: 'Deactivate', danger: true })}
                                >
                                  <UserX size={15} /> Deactivate
                                </button>
                              ) : (
                                <button type="button" className="more-item" onClick={() => patch.mutate({ id: u.id, body: { isActive: true } })}>
                                  <UserCheck size={15} /> Activate
                                </button>
                              )}
                              <button type="button" className="more-item danger-item" onClick={() => ask(`Remove ${u.name}? They lose access to ${me.organization.name}.`, () => remove.mutate(u.id))}>
                                <Trash2 size={15} /> Remove
                              </button>
                            </Menu>
                          </>
                        ) : (
                          <span className="muted" style={{ fontSize: 12.5 }}>
                            <Lock size={13} style={{ verticalAlign: -2 }} /> Owner
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ErrorBox error={users.error ?? patch.error ?? remove.error} />
      </div>
      <div className="card roles-help">
        <strong>Roles</strong>
        {(Object.keys(ROLES) as Role[]).map((r) => (
          <span key={r}>
            <span className="chip" style={{ color: ROLES[r].fg, background: ROLES[r].bg }}>
              {ROLES[r].label}
            </span>{' '}
            {ROLES[r].desc}
          </span>
        ))}
      </div>
      {resetFor && <ResetPassword user={resetFor} onClose={() => setResetFor(null)} />}
    </>
  );
}

function PasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [show, setShow] = useState(true);
  const [copied, setCopied] = useState(false);
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
      <span className="pw-input" style={{ flex: 1 }}>
        <input className="mono" type={show ? 'text' : 'password'} autoComplete="new-password" value={value} onChange={(e) => onChange(e.target.value)} placeholder="At least 10 characters" />
        <button type="button" onClick={() => setShow(!show)} title={show ? 'Hide' : 'Show'} tabIndex={-1}>
          {show ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </span>
      <button type="button" className="btn" onClick={() => onChange(generatePassword())} title="Generate a strong password">
        <RefreshCw size={15} /> Generate
      </button>
      <button
        type="button"
        className="btn"
        disabled={!value}
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function ResetPassword({ user, onClose }: { user: OrgUser; onClose: () => void }) {
  const [pw, setPw] = useState(generatePassword);
  const reset = useMutation({ mutationFn: () => api.post(`/users/${user.id}/password`, { password: pw }) });
  return (
    <Modal
      title={`New password for ${user.name}`}
      onClose={onClose}
      footer={
        reset.isSuccess ? (
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" disabled={pw.length < 10 || reset.isPending} onClick={() => reset.mutate()}>
              <KeyRound size={15} /> Set password
            </button>
          </>
        )
      }
    >
      {reset.isSuccess ? (
        <p>
          The password is changed and {user.name} is signed out everywhere. Give them the new password; they can change it in My account.
        </p>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            {user.email} is signed out everywhere and signs in again with this password.
          </p>
          <PasswordField value={pw} onChange={setPw} />
        </>
      )}
      <ErrorBox error={reset.error} />
    </Modal>
  );
}

/** Add or edit a user (a page, not a dialog). */
export function UserEditPage() {
  const { id } = useParams();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: OrgUser[] }>('/users').then((r) => r.items), enabled: id !== 'new' });
  if (!id || id === 'new') return <UserEditor initial={null} />;
  if (users.isLoading) return <Loading />;
  const u = users.data?.find((x) => x.id === id);
  if (!u) return <div className="card empty">This user was not found.</div>;
  return <UserEditor key={u.id} initial={u} />;
}

function UserEditor({ initial }: { initial: OrgUser | null }) {
  const { me, sites } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'OPERATOR');
  const [allSites, setAllSites] = useState(!initial || initial.siteIds.length === 0);
  const [siteIds, setSiteIds] = useState<string[]>(initial?.siteIds ?? []);
  const [pw, setPw] = useState(() => (initial ? '' : generatePassword()));
  const [created, setCreated] = useState<{ existing: boolean } | null>(null);
  const roles = (Object.keys(ROLES) as Role[]).filter((r) => RANK[r] <= RANK[me.role] && (r !== 'OWNER' || me.role === 'OWNER'));
  const save = useMutation({
    mutationFn: () => {
      const s = allSites ? [] : siteIds;
      return initial
        ? api.patch(`/users/${initial.id}`, { name: name.trim(), role, siteIds: s })
        : api.post<{ existingAccount: boolean }>('/users', { name: name.trim(), email: email.trim(), role, siteIds: s, password: pw });
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      if (initial) navigate('/account/users');
      else setCreated({ existing: !!(r as { existingAccount?: boolean } | undefined)?.existingAccount });
    },
  });
  const valid = name.trim() && (initial || (/^\S+@\S+\.\S+$/.test(email.trim()) && pw.length >= 10)) && (allSites || siteIds.length > 0);

  if (created)
    return (
      <>
        <PageHeader title="User added" sub={`${name} can now sign in to the PulseIPTV admin.`} />
        <div className="card" style={{ maxWidth: 640 }}>
          {created.existing ? (
            <p>
              <strong>{email}</strong> already had a PulseIPTV account, so they sign in with their current password.
            </p>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                Give <strong>{email}</strong> this first password. It is not shown again; they can change it in My account.
              </p>
              <PasswordField value={pw} onChange={() => undefined} />
            </>
          )}
          <div className="row" style={{ gap: 8, marginTop: 16 }}>
            <button className="btn primary" onClick={() => navigate('/account/users')}>
              Back to users
            </button>
          </div>
        </div>
      </>
    );

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={() => navigate('/account')}>
          Account settings
        </button>
        <ChevronRight size={14} />
        <button type="button" onClick={() => navigate('/account/users')}>
          Users
        </button>
        <ChevronRight size={14} />
        <strong>{initial ? 'Edit user' : 'Add user'}</strong>
      </div>
      <PageHeader
        title={initial ? `Edit user · ${initial.name}` : 'Add user'}
        sub={initial ? initial.email : 'They sign in at this address with their email and the password you give them.'}
        actions={
          <>
            <button className="btn" onClick={() => navigate('/account/users')}>
              <ArrowLeft size={16} /> Back to users
            </button>
            <button className="btn primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
              <Check size={16} /> {save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Add user'}
            </button>
          </>
        }
      />
      <ErrorBox error={save.error} />
      <div className="account-grid" style={{ alignItems: 'start' }}>
        <div className="card">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <UserRound size={22} />
            </span>
            <span className="pkg-head-title">Details</span>
          </div>
          <div className="pw-form">
            <label>
              <span className="camp-label">Full name</span>
              <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sara Haddad" autoFocus={!initial} />
            </label>
            <label>
              <span className="camp-label">Email address</span>
              <input type="email" value={email} disabled={!!initial} onChange={(e) => setEmail(e.target.value)} placeholder="name@hotel.com" />
              {initial && <span className="camp-hint">The email is the sign-in name and cannot be changed.</span>}
            </label>
            {!initial && (
              <label>
                <span className="camp-label">First password</span>
                <PasswordField value={pw} onChange={setPw} />
                <span className="camp-hint">You give it to the user; they can change it in My account. Not used if the email already has an account.</span>
              </label>
            )}
          </div>
        </div>
        <div className="stack">
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <ShieldCheck size={22} />
              </span>
              <span className="pkg-head-title">Role</span>
            </div>
            <div className="role-pick">
              {roles.map((r) => (
                <button key={r} type="button" className={role === r ? 'on' : ''} onClick={() => setRole(r)}>
                  <span className="chip" style={{ color: ROLES[r].fg, background: ROLES[r].bg }}>
                    {ROLES[r].label}
                  </span>
                  <span>{ROLES[r].desc}</span>
                  {role === r && <Check size={16} />}
                </button>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Lock size={22} />
              </span>
              <span className="pkg-head-title">Site access</span>
            </div>
            <div className="role-pick">
              <button type="button" className={allSites ? 'on' : ''} onClick={() => setAllSites(true)}>
                <strong>All sites</strong>
                <span>Including sites added later</span>
                {allSites && <Check size={16} />}
              </button>
              <button type="button" className={!allSites ? 'on' : ''} onClick={() => setAllSites(false)}>
                <strong>Only some sites</strong>
                <span>Pick them below</span>
                {!allSites && <Check size={16} />}
              </button>
            </div>
            {!allSites && (
              <div className="row" style={{ gap: 6, marginTop: 12 }}>
                {sites.map((s) => {
                  const on = siteIds.includes(s.id);
                  return (
                    <button key={s.id} type="button" className={`pick-chip${on ? ' on' : ''}`} onClick={() => setSiteIds(on ? siteIds.filter((x) => x !== s.id) : [...siteIds, s.id])}>
                      {s.name}
                    </button>
                  );
                })}
                {siteIds.length === 0 && <span className="camp-hint err">Pick at least one site.</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
